import {
  type UnitInsightChatResponse,
  type UnitInsightLlmChatsResponseFilePart,
  type UnitInsightLlmChatsResponseTextPart,
} from '../../../../api/customEndpoints/getUnitInsightLlmChat';
import { type PropsWithChildren, useMemo } from 'react';
import { Badge } from '../../../../components/ui/badge';
import { buttonVariants } from '../../../../components/ui/button';
import { FileText } from 'lucide-react';
import { cn } from '../../../../lib/utils';
import { useTranslate } from 'ra-core';

export function ChatMessage({
  message,
}: {
  message: UnitInsightChatResponse['data'][number];
}) {
  if (message.contents.role === 'assistant') {
    return (
      <AssistantMessage>
        <MessageContents contents={message.contents} />
      </AssistantMessage>
    );
  }

  return (
    <UserMessage>{<MessageContents contents={message.contents} />}</UserMessage>
  );
}

function AssistantMessage({ children }: PropsWithChildren) {
  return <div>{children}</div>;
}

function UserMessage({ children }: PropsWithChildren) {
  return <div className="w-4/5 self-end rounded-md border p-4">{children}</div>;
}

function MessageContents({
  contents,
}: {
  contents: UnitInsightChatResponse['data'][number]['contents'];
}) {
  const translate = useTranslate();
  const groupedParts = useMemo(() => {
    const groups: (
      | UnitInsightLlmChatsResponseTextPart
      | UnitInsightLlmChatsResponseFilePart[]
    )[] = [];

    for (const part of contents.parts) {
      if ('text' in part) {
        groups.push(part);
      } else {
        const lastGroup = groups.at(-1);
        if (Array.isArray(lastGroup)) {
          lastGroup.push(part);
        } else {
          groups.push([part]);
        }
      }
    }

    return groups;
  }, [contents]);

  if (groupedParts.length === 0) {
    return <span>{translate('moonstar.unitPerformance.chat.noContent')}</span>;
  }

  return (
    <div className="space-y-4">
      {groupedParts.map((group, index) => {
        if (Array.isArray(group)) {
          return <MessageFiles key={index} parts={group} />;
        } else {
          return <MessageTextPart key={index} part={group} />;
        }
      })}
    </div>
  );
}

function MessageTextPart({
  part,
}: {
  part: UnitInsightLlmChatsResponseTextPart;
}) {
  return <div className="whitespace-pre-line">{part.text}</div>;
}

function MessageFiles({
  parts,
}: {
  parts: UnitInsightLlmChatsResponseFilePart[];
}) {
  const translate = useTranslate();

  return (
    <div className="space-y-2">
      <h3 className="font-semibold">
        {translate('moonstar.unitPerformance.chat.attachments')}
      </h3>
      <div className="grid gap-2 [grid-template-columns:repeat(auto-fit,minmax(min(100%,12rem),1fr))]">
        {parts.map((part, index) => (
          <MessageFilePart key={index} part={part} />
        ))}
      </div>
    </div>
  );
}

function MessageFilePart({
  part,
}: {
  part: UnitInsightLlmChatsResponseFilePart;
}) {
  const fileName = part.fileData.fileUri.split('/').at(-1) ?? 'attachment';
  const fileExtension = fileName.split('.').at(-1)?.toUpperCase() ?? 'FILE';

  return (
    <a
      className={cn(
        buttonVariants({ variant: 'outline', size: 'sm' }),
        'min-w-0 justify-start'
      )}
      href={part.fileData.fileUri}
      target="_blank"
      rel="noopener noreferrer"
      title={fileName}
    >
      <FileText />
      <Badge variant="secondary">{fileExtension}</Badge>
      <span className="truncate">{fileName}</span>
    </a>
  );
}
